import { useCallback, useState } from "react";
import axios from "axios";
import { API_DOMAIN } from "config";

export default function useDealerPaymentMethods() {
  const [methods, setMethods] = useState([]);
  const [defaultMethodId, setDefaultMethodId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchMethods = useCallback(async (userId) => {
    if (!userId) return;
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API_DOMAIN}/billing/dealerPaymentMethods/${userId}`);
      const data = res?.data?.data;
      const paymentMethods = data?.payment_methods || [];
      const resolvedDefaultId = data?.default_payment_method_id || paymentMethods[0]?.id || null;
      setMethods(
        paymentMethods.map((method) => ({
          ...method,
          is_default: Number(method.id) === Number(resolvedDefaultId) ? 1 : 0,
        }))
      );
      setDefaultMethodId(resolvedDefaultId ? Number(resolvedDefaultId) : null);
      return { ...data, payment_methods: paymentMethods, default_payment_method_id: resolvedDefaultId };
    } catch {
      setError("לא ניתן לטעון אמצעי תשלום כרגע.");
      setMethods([]);
      setDefaultMethodId(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const setDefault = useCallback(async (userId, methodId) => {
    await axios.post(`${API_DOMAIN}/billing/setDefaultDealerPaymentMethod`, {
      user_id: userId,
      method_id: methodId,
    });
    setMethods((prev) =>
      prev.map((m) => ({ ...m, is_default: Number(m.id) === Number(methodId) ? 1 : 0 }))
    );
    setDefaultMethodId(Number(methodId));
  }, []);

  const removeMethod = useCallback(async (userId, methodId) => {
    await axios.post(`${API_DOMAIN}/billing/removeDealerPaymentMethod`, {
      user_id: userId,
      method_id: methodId,
    });
    setMethods((prev) => {
      const remaining = prev.filter((m) => Number(m.id) !== Number(methodId));
      // אם הוסר ה-default ויש כרטיסים אחרים — הראשון ברשימה הוא default
      if (remaining.length > 0 && !remaining.some((m) => m.is_default)) {
        remaining[0] = { ...remaining[0], is_default: 1 };
        setDefaultMethodId(Number(remaining[0].id));
      } else if (remaining.length === 0) {
        setDefaultMethodId(null);
      }
      return remaining;
    });
  }, []);

  const addMethod = useCallback((paymentMethod) => {
    setMethods((prev) => {
      const exists = prev.some((m) => Number(m.id) === Number(paymentMethod?.id));
      if (!paymentMethod?.id) return prev;

      setDefaultMethodId(Number(paymentMethod.id));

      if (exists) {
        return prev.map((m) => ({
          ...m,
          is_default: Number(m.id) === Number(paymentMethod.id) ? 1 : 0,
        }));
      }

      const newMethod = { ...paymentMethod, is_default: 1 };
      return [newMethod, ...prev.map((m) => ({ ...m, is_default: 0 }))];
    });
  }, []);

  return {
    methods,
    defaultMethodId,
    loading,
    error,
    fetchMethods,
    setDefault,
    removeMethod,
    addMethod,
  };
}
